import { DATE_FORMAT, DATE_FORMAT_SHORT, WebTable } from '@constants';
import DataTable from '@containers/DataTable';
import { Prototype } from '@core';
import { useFetchDashboardSTCWaitingTime, useLocalTable } from '@hooks';
import { onShowClient } from '@pages/Client/helpers';
import { onShowEquipment } from '@pages/Equipment/helpers';
import { onShowStaff } from '@pages/Staff/helpers';
import { onShowTransportJob } from '@pages/TransportJob/helpers';
import { TableUtils } from '@utils';
import { ChartOptions } from 'chart.js';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import trans from 'translation';
import { KContainer, KInput, KLabel, KLoading } from 'uikit';

interface IProps {
  currentDate: Date;
}

enum DisplayMode {
  Table = 'table',
  Chart = 'chart'
}

const STCWaitingTime = ({ currentDate }: IProps) => {
  const { data, isLoading, refetch } = useFetchDashboardSTCWaitingTime(
    Prototype.date.formatDB(currentDate as any)
  );

  const [displayMode, setDisplayMode] = useState(DisplayMode.Table);

  const columns = useMemo(
    () => [
      {
        label: trans('container_number'),
        name: 'displayContainerNumber',
        options: {
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text
                  onPress={() => onShowTransportJob(item.transportJob?.id)}
                >
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('schedule_trip'),
        name: 'scheduleTrip'
        // options: {
        //   customBodyRenderLite: (idx: number) => {
        //     const item = data?.[idx];
        //     if (item) {
        //       const start = Prototype.date.formatT(item.timeStart);
        //       const end = Prototype.date.formatT(item.timeEnd);
        //       return (
        //         <KLabel.Text>
        //           {start} - {end}
        //         </KLabel.Text>
        //       );
        //     }
        //     return 'N/A';
        //   }
        // }
      },
      {
        label: trans('driver'),
        name: 'driverDisplayName',
        options: {
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text onPress={() => onShowStaff(item.driverId)}>
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('location'),
        name: 'locationDisplay',
        options: {
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text
                  onPress={() => onShowClient(item.locationClientId)}
                >
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('checkin_checkout'),
        name: 'checkInCheckOut'
        // options: {
        //   customBodyRenderLite: (idx: number) => {
        //     const item = data?.[idx];
        //     if (item) {
        //       const checkIn = Prototype.date.formatT(
        //         item.actualCheckinDatetime
        //       );
        //       const checkOut = Prototype.date.formatT(
        //         item.actualCheckoutDatetime
        //       );
        //       return (
        //         <KLabel.Text>
        //           {checkIn} - {checkOut}
        //         </KLabel.Text>
        //       );
        //     }
        //     return 'N/A';
        //   }
        // }
      },
      {
        label: trans('free_time'),
        name: 'freeTime',
        options: TableUtils.options.withUnit('mins')
      },
      {
        label: trans('waiting_time'),
        name: 'waitingTime',
        options: TableUtils.options.withUnit('mins')
      },
      {
        label: trans('routing_from'),
        name: 'jobContainerRoutingStartDisplay',
        options: {
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text
                  onPress={() =>
                    onShowClient(item.jobContainerRoutingStartClientId)
                  }
                >
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('routing_to'),
        name: 'jobContainerRoutingEndDisplay',
        options: {
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text
                  onPress={() =>
                    onShowClient(item.jobContainerRoutingEndClientId)
                  }
                >
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('vehicle'),
        name: 'truckDisplayName',
        options: {
          ...TableUtils.options.baseXLg,
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text onPress={() => onShowEquipment(item.truckId)}>
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('trailer'),
        name: 'trailerDisplayName',
        options: {
          ...TableUtils.options.baseXLg,
          customBodyRender: (v: string, rowData: any) => {
            const item = data?.[rowData.rowIndex];
            if (item) {
              return (
                <KLabel.Text onPress={() => onShowEquipment(item.trailerId)}>
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('job_number'),
        name: 'transportJob.jobNumber',
        options: {
          ...TableUtils.options.baseMd,
          customBodyRender: (v: any, rowData: any) => {
            const item = data?.[rowData?.rowIndex];
            if (v) {
              return (
                <KLabel.Text
                  onPress={() => onShowTransportJob(item?.transportJob.id)}
                >
                  {v}
                </KLabel.Text>
              );
            }
            return 'N/A';
          }
        }
      },
      {
        label: trans('job_type'),
        name: 'transportJob.jobType'
      },
      {
        label: trans('vessel'),
        name: 'transportJob.vessel.vessel',
        options: TableUtils.options.baseMd
      },
      {
        label: trans('voyage'),
        name: 'transportJob.voyage'
      },
      {
        label: trans('eta'),
        name: 'transportJobEta'
        // name: 'transportJob.eta',
        // options: TableUtils.options.date()
      },
      {
        label: trans('etd'),
        name: 'transportJobEtd'
        // name: 'transportJob.etd',
        // options: TableUtils.options.date()
      },
      {
        label: trans('imp_available_date'),
        name: 'transportJobAvail'
        // name: 'transportJob.avail',
        // options: TableUtils.options.date()
      },
      {
        label: trans('1st_free_date'),
        name: 'transportJobFirstFreeDate'
        // name: 'transportJob.firstFreeDate',
        // options: TableUtils.options.date(DATE_FORMAT_SHORT)
      },
      {
        label: trans('imp_storage_start_date'),
        name: 'transportJobStor'
        // name: 'transportJob.stor',
        // options: TableUtils.options.date()
      },
      {
        label: trans('discharge_date'),
        name: 'transportJobFirstDischargeDate'
        // name: 'transportJob.firstDischargeDate',
        // options: TableUtils.options.date(DATE_FORMAT_SHORT)
      },
      {
        label: trans('exp_receival_commencement_date'),
        name: 'transportJobExpRecvDate'
        // name: 'transportJob.expRecvDate',
        // options: TableUtils.options.date()
      },
      {
        label: trans('cargo_cutoff_date'),
        name: 'transportJobCutOffDate'
        // name: 'transportJob.cutOffDate',
        // options: TableUtils.options.date()
      }
    ],
    [data]
  );

  const table = useLocalTable({
    name: WebTable.OTHER,
    columns,
    data,
    otherOptions: {
      selectableRows: 'none',
      tableBodyMaxHeight: '80vh',
      pagination: false
    },
    onRefresh: refetch
  });

  const onNormalizeData = useCallback((_data: any[]) => {
    return _data.map((_d: any) => {
      const start = Prototype.date.formatT(_d.timeStart);
      const end = Prototype.date.formatT(_d.timeEnd);
      const checkIn = Prototype.date.formatT(_d.actualCheckinDatetime);
      const checkOut = Prototype.date.formatT(_d.actualCheckoutDatetime);
      return {
        ..._d,
        scheduleTrip: `${start} - ${end}`,
        checkInCheckOut: `${checkIn} - ${checkOut}`,
        transportJobEta: Prototype.date
          .toLocal(_d?.transportJob?.eta)
          ?.format(DATE_FORMAT),
        transportJobEtd: Prototype.date
          .toLocal(_d?.transportJob?.etd)
          ?.format(DATE_FORMAT),
        transportJobAvail: Prototype.date
          .toLocal(_d?.transportJob?.avail)
          ?.format(DATE_FORMAT),
        transportJobFirstFreeDate: Prototype.date
          .toLocal(_d?.transportJob?.firstFreeDate)
          ?.format(DATE_FORMAT_SHORT),
        transportJobStor: Prototype.date
          .toLocal(_d?.transportJob?.stor)
          ?.format(DATE_FORMAT),
        transportJobFirstDischargeDate: Prototype.date
          .toLocal(_d?.transportJob?.firstDischargeDate)
          ?.format(DATE_FORMAT_SHORT),
        transportJobExpRecvDate: Prototype.date
          .toLocal(_d?.transportJob?.expRecvDate)
          ?.format(DATE_FORMAT),
        transportJobCutOffDate: Prototype.date
          .toLocal(_d?.transportJob?.cutOffDate)
          ?.format(DATE_FORMAT)
      };
    });
  }, []);

  const chartData = useMemo(() => {
    const labels = data?.map(d => {
      return d?.displayContainerNumber ?? trans('unknown');
    });
    const _data = data?.map(item => item.waitingTime ?? 0);
    return {
      labels: labels,
      datasets: [
        {
          label: trans('waiting_time'),
          data: _data,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)'
          // yAxisID: 'y'
        }
      ]
    };
  }, [data]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      // stacked: false,
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: context => {
              const value = context.parsed.y;
              return `${context.dataset.label}: ${value} min`;
            }
          }
        }
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'bottom',
          title: {
            display: true,
            text: 'Minutes',
            font: {
              size: 14
            }
          },
          ticks: {
            callback: (value: number) => `${value} min`
          }
        },
        x: {
          type: 'category',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Driver',
            font: {
              size: 14
            }
          },
          ticks: {
            callback: (v: string, index: number) => {
              return data?.[index]?.driverDisplayName ?? trans('unknown');
            }
          }
        }
      }
    }),
    [data]
  );

  return (
    <KContainer.View height="100%" marginT={0}>
      <KContainer.View
        row
        justifyContent="flex-end"
        alignItems="center"
        // marginB="1rem"
        paddingV={'0.5rem'}
      >
        <KInput.TextField
          width={200}
          label={trans('displayed_by')}
          value={displayMode}
          onChange={(v: any) => setDisplayMode(v.target.value)}
          options={[
            {
              key: DisplayMode.Table,
              label: trans('table')
            },
            {
              key: DisplayMode.Chart,
              label: trans('chart')
            }
          ]}
        />
      </KContainer.View>
      <KContainer.View hidden={displayMode !== DisplayMode.Table}>
        <DataTable
          {...table}
          isLoading={isLoading}
          onNormalizeData={onNormalizeData}
        />
      </KContainer.View>
      <KContainer.View hidden={displayMode !== DisplayMode.Chart}>
        {isLoading && <KLoading />}
        <Line
          data={chartData}
          options={options}
          style={{
            minHeight: '50vh'
          }}
        />
      </KContainer.View>
    </KContainer.View>
  );
};

export default memo(STCWaitingTime);
